import React from 'react';
import { useMemo, useState } from 'react';
import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import moment from 'moment';
import * as XLSX from 'xlsx';
import CustomButton from 'components/CustomButton';
import colors from 'config/colors';
import EmptyState from 'views/admin/Common/EmptyState';
import StatTile from 'views/admin/Common/StatTile';
import { SkeletonBars } from 'views/admin/Common/Loader';

const TONE_COLORS = { bad: '#c62828', warn: '#e65100' };
const PAGE_SIZE = 25;

// A report as summary tiles + a table with an "Export Excel" button (or, with `renderRows(pageRows)`, the rows drawn as the caller
// likes - e.g. cards - under the same tiles, paging and export). `rows` hold plain text and optional
// `tones: { columnKey: 'bad' | 'warn' }`, so what's exported is exactly what's shown.
const ReportTable = ({
  title,
  columns,
  rows,
  summary = [],
  fileName,
  emptyText = 'Nothing to show.',
  filters,
  renderers = {},
  renderRows,
  loading = false,
}) => {
  const [page, setPage] = useState(0);
  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount - 1);
  const pageRows = useMemo(
    () => rows.slice(currentPage * PAGE_SIZE, currentPage * PAGE_SIZE + PAGE_SIZE),
    [rows, currentPage]
  );

  const exportExcel = () => {
    const data = rows.map(row => columns.reduce((acc, col) => ({ ...acc, [col.label]: row[col.key] }), {}));
    const sheet = XLSX.utils.json_to_sheet(data, { header: columns.map(col => col.label) });
    sheet['!cols'] = columns.map(col => ({
      wch: Math.min(40, Math.max(col.label.length, ...rows.map(row => `${row[col.key]}`.length)) + 2),
    }));
    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, sheet, title.slice(0, 31));
    XLSX.writeFile(book, `${fileName}_${moment().format('YYYY-MM-DD')}.xlsx`);
  };

  return (
    <div>
      {!!summary.length && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
          {summary.map(item => (
            <StatTile
              key={item.label}
              label={item.label}
              value={item.value}
              tone={item.value ? item.tone : 'neutral'}
            />
          ))}
        </div>
      )}
      <Paper square style={{ padding: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <b style={{ color: colors.accentDark }}>{title}</b>
          <CustomButton
            size="small"
            variant="outlined"
            name="Export Excel"
            disabled={!rows.length}
            onClick={exportExcel}
          />
        </div>
        {filters && <div style={{ marginBottom: 8 }}>{filters}</div>}
        {loading && !rows.length ? (
          <SkeletonBars bars={5} />
        ) : rows.length ? (
          <>
            {renderRows ? (
              renderRows(pageRows)
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {columns.map(col => (
                        <TableCell key={col.key} style={{ color: '#7d7d7d', fontSize: 12, whiteSpace: 'nowrap' }}>
                          <b>{col.label}</b>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pageRows.map((row, index) => (
                      <TableRow key={index} hover>
                        {columns.map(col => {
                          const tone = row.tones?.[col.key];
                          return (
                            <TableCell
                              key={col.key}
                              style={{ color: TONE_COLORS[tone], fontWeight: tone ? 'bold' : undefined, fontSize: 13 }}>
                              {renderers[col.key] ? renderers[col.key](row) : row[col.key]}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
            {pageCount > 1 && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
                <span style={{ fontSize: 12, color: '#7d7d7d' }}>
                  {currentPage * PAGE_SIZE + 1} - {Math.min(rows.length, (currentPage + 1) * PAGE_SIZE)} of{' '}
                  {rows.length}
                </span>
                <CustomButton
                  size="small"
                  variant="outlined"
                  color="default"
                  name="Prev"
                  disabled={currentPage === 0}
                  onClick={() => setPage(currentPage - 1)}
                />
                <CustomButton
                  size="small"
                  variant="outlined"
                  color="default"
                  name="Next"
                  disabled={currentPage >= pageCount - 1}
                  onClick={() => setPage(currentPage + 1)}
                />
              </div>
            )}
          </>
        ) : (
          <EmptyState compact bare icon="far fa-folder-open" title={emptyText} />
        )}
      </Paper>
    </div>
  );
};

export default ReportTable;
